import { z } from 'zod';
import { UserRoleSchema } from '@duke-hq/svc-api-db/schemas/enums/UserRole.schema';
import { StatesSchema } from '@duke-hq/svc-api-db/schemas/enums/States.schema';

import capitalize from 'lodash-es/capitalize.js';

export const ConsultantRoleSchema = z.enum([
  UserRoleSchema.enum.NATIONAL_MANAGER,
  UserRoleSchema.enum.STATE_MANAGER,
  UserRoleSchema.enum.REGIONAL_MANAGER,
  UserRoleSchema.enum.SITE_MANAGER,
  UserRoleSchema.enum.CONSULTANT,
]);

export const ConsultantRoles: ConsultantRoleType[] = [
  ConsultantRoleSchema.enum.NATIONAL_MANAGER,
  ConsultantRoleSchema.enum.STATE_MANAGER,
  ConsultantRoleSchema.enum.REGIONAL_MANAGER,
  ConsultantRoleSchema.enum.SITE_MANAGER,
  ConsultantRoleSchema.enum.CONSULTANT,
];

export const ConsultantRoleColors: Record<ConsultantRoleType, string> = {
  [ConsultantRoleSchema.enum.NATIONAL_MANAGER]: 'blue.600',
  [ConsultantRoleSchema.enum.REGIONAL_MANAGER]: 'orange.600',
  [ConsultantRoleSchema.enum.STATE_MANAGER]: 'yellow.600',
  [ConsultantRoleSchema.enum.SITE_MANAGER]: 'green.600',
  [ConsultantRoleSchema.enum.CONSULTANT]: 'purple.600',
};

export type ConsultantSelectOption = {
  value: ConsultantRoleType;
  label: string;
};

export function getRolesAvailable(
  role: ConsultantRoleType | null = null,
): ConsultantSelectOption[] {
  const rolesToMap = role ? ConsultantRoles.slice(ConsultantRoles.indexOf(role)) : ConsultantRoles;

  return rolesToMap.map((role) => ({
    value: role,
    label: capitalize(role.replace(/_/g, ' ')),
  }));
}

export type ConsultantRoleType = z.infer<typeof ConsultantRoleSchema>;

export const SiteOptionSchema = z.object({
  label: z.string(),
  value: z.object({
    site_id: z.string(),
    site_name: z.string(),
    site_state: StatesSchema,
  }),
});

export type SiteOption = z.infer<typeof SiteOptionSchema>;

export const ConsultantOptionSchema = z.object({
  role: ConsultantRoleSchema,
  email: z.string(),
  value: z.string(),
  label: z.string(),
  fullname: z.string(),
});

export type ConsultantOption = z.infer<typeof ConsultantOptionSchema>;

/**
 * Determines if a user has authority over another role based on role hierarchy.
 * Users have authority over roles at the same level or below, never above.
 * Role hierarchy (highest to lowest): NATIONAL_MANAGER > STATE_MANAGER > REGIONAL_MANAGER > SITE_MANAGER > CONSULTANT
 *
 * @param currentUserRole - The role of the user with authority
 * @param targetRole - The role being checked for authority
 * @returns true if the current user has authority over the target role, false otherwise
 */
export function hasRoleAuthority(
  currentUserRole: ConsultantRoleType,
  targetRole: ConsultantRoleType,
): boolean {
  const currentRoleIndex = ConsultantRoles.indexOf(currentUserRole);
  const targetRoleIndex = ConsultantRoles.indexOf(targetRole);

  // Has authority if target role is at same level or lower (higher index)
  return targetRoleIndex >= currentRoleIndex;
}
